import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getTeachers, createTeacher, updateTeacher, deleteTeacher } from '../../apis/teachers';
import { RootState } from '../store';

export interface Teacher {
    id: string;
    full_name: string;
    email: string;
    phone_number?: string;
    bio?: string;
    profile_picture_url?: string;
    total_credits?: number;
    deleted_at?: string | null;
    lesson_count?: number;
    student_count?: number;
    profile?: any;
}

interface TeacherState {
    teachers: Teacher[];
    total: number;
    page: number;
    size: number;
    pages: number;
    loading: boolean;
    error: string | null;
}

const initialState: TeacherState = {
    teachers: [],
    total: 0,
    page: 1,
    size: 12,
    pages: 1,
    loading: false,
    error: null,
};

// Thunks
export const fetchTeachers = createAsyncThunk(
    'teachers/fetchTeachers',
    async (params: Record<string, any> = {}, { rejectWithValue }) => {
        try {
            const response = await getTeachers(params);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not fetch teachers');
            return rejectWithValue(errorText);
        }
    }
);

export const addTeacher = createAsyncThunk(
    'teachers/addTeacher',
    async (data: Partial<Teacher>, { rejectWithValue }) => {
        try {
            const response = await createTeacher(data);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not add teacher');
            return rejectWithValue(errorText);
        }
    }
);

export const editTeacher = createAsyncThunk(
    'teachers/editTeacher',
    async ({ id, data }: { id: string; data: Partial<Teacher> }, { rejectWithValue }) => {
        try {
            const response = await updateTeacher(id, data);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not edit teacher');
            return rejectWithValue(errorText);
        }
    }
);

export const removeTeacher = createAsyncThunk(
    'teachers/removeTeacher',
    async (id: string, { rejectWithValue }) => {
        try {
            await deleteTeacher(id);
            return id;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not delete teacher');
            return rejectWithValue(errorText);
        }
    }
);

const teacherSlice = createSlice({
    name: 'teachers',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchTeachers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTeachers.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.teachers = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.size = action.payload.size;
                state.pages = action.payload.pages;
            })
            .addCase(fetchTeachers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(addTeacher.fulfilled, (state, action: PayloadAction<Teacher>) => {
                state.teachers.push(action.payload);
            })
            .addCase(editTeacher.fulfilled, (state, action: PayloadAction<Teacher>) => {
                const index = state.teachers.findIndex((t) => t.id === action.payload.id);
                if (index !== -1) state.teachers[index] = action.payload;
            })
            .addCase(removeTeacher.fulfilled, (state, action: PayloadAction<string>) => {
                state.teachers = state.teachers.filter((t) => t.id !== action.payload);
            });
    },
});

export const selectAllTeachers = (state: RootState) => state.teachers.teachers;
export const selectTeacherById = (state: RootState, id: string) =>
    state.teachers.teachers.find((t) => t.id === id);

export default teacherSlice.reducer;
